import React from 'react';

/**
 * Image size presets
 */
export const IMAGE_SIZES = {
  small: { label: 'Small', maxWidth: '200px' },
  medium: { label: 'Medium', maxWidth: '400px' },
  large: { label: 'Large', maxWidth: '800px' },
  full: { label: 'Full', maxWidth: '100%' }
};

/**
 * Image crop/fit presets
 */
export const IMAGE_FITS = {
  contain: { label: 'Fit', value: 'contain' },
  cover: { label: 'Fill', value: 'cover' },
  none: { label: 'Original', value: 'none' }
};

/**
 * Default image settings
 */
export const DEFAULT_IMAGE_SETTINGS = {
  size: 'large',
  fit: 'cover',
  aspectRatio: '16/9'
};

/**
 * Aspect ratio presets
 */
export const ASPECT_RATIOS = {
  '1/1': { label: 'Square', value: '1/1' },
  '4/3': { label: '4:3', value: '4/3' },
  '16/9': { label: '16:9', value: '16/9' },
  '3/2': { label: '3:2', value: '3/2' },
  'auto': { label: 'Auto', value: 'auto' }
};

/**
 * Normalize image source - handles URLs, image IDs, and API paths
 */
export const normalizeImageSrc = (src) => {
  if (!src) return null;
  
  // Already a full URL
  if (typeof src === 'string' && (src.startsWith('http://') || src.startsWith('https://'))) {
    return src;
  }
  
  // Numeric ID - use the images API
  if (typeof src === 'number' || (typeof src === 'string' && /^\d+$/.test(src))) {
    return `/api/images/${src}`;
  }
  
  // Already an API path
  if (typeof src === 'string' && src.startsWith('/api/')) {
    return src;
  }
  
  // Static folder path
  if (typeof src === 'string' && src.startsWith('/static/')) {
    return src;
  }
  
  // Handle /images/ paths - normalize extension case to lowercase
  if (typeof src === 'string' && src.startsWith('/images/')) {
    return src.replace(/\.(JPG|JPEG|PNG|GIF|WEBP)$/i, (match) => match.toLowerCase());
  }
  
  // Relative path
  if (typeof src === 'string') {
    if (!src.startsWith('/')) {
      return `/static/${src}`;
    }
    return src;
  }
  
  return null;
};

/**
 * Get inline styles for an image based on settings
 */
export const getImageStyles = (settings = {}) => {
  const { size = 'large', fit = 'cover', aspectRatio = 'auto' } = settings;
  
  const styles = {
    width: '100%',
    height: 'auto',
    display: 'block',
    margin: '0 auto'
  };
  
  if (aspectRatio !== 'auto') {
    styles.aspectRatio = aspectRatio;
    styles.objectFit = fit;
  }
  
  return styles;
};

/**
 * Image Controls Component - renders size/crop controls for a block
 */
export const ImageControlsPanel = ({ 
  settings = DEFAULT_IMAGE_SETTINGS, 
  onChange,
  showAspectRatio = true,
  showImage = true,
  onToggleShow = null
}) => {
  const handleChange = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  const controlStyle = {
    background: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    padding: '4px 8px',
    fontSize: '12px',
    cursor: 'pointer'
  };

  const selectStyle = {
    ...controlStyle,
    // Dark background for dropdown options
    colorScheme: 'dark'
  };

  const labelStyle = {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase'
  };

  return (
    <div className="image-controls" style={{
      display: 'flex',
      gap: '12px',
      padding: '8px 0',
      borderTop: '1px solid rgba(255,255,255,0.1)',
      marginTop: '8px',
      flexWrap: 'wrap',
      alignItems: 'center'
    }}>
      {/* Show/Hide Toggle */}
      {onToggleShow && (
        <button
          onClick={onToggleShow}
          style={{
            ...controlStyle,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: showImage ? 'rgba(6, 214, 160, 0.2)' : 'rgba(255,255,255,0.1)',
            borderColor: showImage ? 'rgba(6, 214, 160, 0.4)' : 'rgba(255,255,255,0.2)'
          }}
          title={showImage ? 'Hide image in email' : 'Show image in email'}
        >
          <span>{showImage ? '👁' : '👁‍🗨'}</span>
          <span style={{ fontSize: '11px' }}>{showImage ? 'Visible' : 'Hidden'}</span>
        </button>
      )}

      {/* Size Control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label style={labelStyle}>Size</label>
        <select
          value={settings.size || 'large'}
          onChange={(e) => handleChange('size', e.target.value)}
          style={selectStyle}
        >
          {Object.entries(IMAGE_SIZES).map(([key, { label }]) => (
            <option key={key} value={key} style={{ background: '#1a1a1a', color: '#fff' }}>{label}</option>
          ))}
        </select>
      </div>

      {/* Fit/Crop Control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label style={labelStyle}>Fit</label>
        <select
          value={settings.fit || 'cover'}
          onChange={(e) => handleChange('fit', e.target.value)}
          style={selectStyle}
        >
          {Object.entries(IMAGE_FITS).map(([key, { label }]) => (
            <option key={key} value={key} style={{ background: '#1a1a1a', color: '#fff' }}>{label}</option>
          ))}
        </select>
      </div>

      {/* Aspect Ratio Control */}
      {showAspectRatio && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={labelStyle}>Ratio</label>
          <select
            value={settings.aspectRatio || 'auto'}
            onChange={(e) => handleChange('aspectRatio', e.target.value)}
            style={selectStyle}
          >
            {Object.entries(ASPECT_RATIOS).map(([key, { label }]) => (
              <option key={key} value={key} style={{ background: '#1a1a1a', color: '#fff' }}>{label}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

/**
 * Controlled Image Component - renders image with settings applied
 */
export const ControlledImage = ({ 
  src, 
  alt, 
  settings = DEFAULT_IMAGE_SETTINGS,
  style = {},
  className = '',
  onError = null
}) => {
  const normalizedSrc = normalizeImageSrc(src);
  // Merge with defaults to ensure all properties exist
  const mergedSettings = { ...DEFAULT_IMAGE_SETTINGS, ...settings };
  const imageStyles = getImageStyles(mergedSettings);
  
  if (!normalizedSrc) return null;
  
  return (
    <img
      src={normalizedSrc}
      alt={alt || ''}
      className={className}
      style={{ ...imageStyles, ...style }}
      loading="lazy"
      onError={onError}
    />
  );
};

export default {
  ImageControlsPanel,
  ControlledImage,
  normalizeImageSrc,
  getImageStyles,
  IMAGE_SIZES,
  IMAGE_FITS,
  ASPECT_RATIOS,
  DEFAULT_IMAGE_SETTINGS
};
